import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { MATCH_DISCLAIMER, type CaseView, type MatchInput, type MatchResponse, type MatchResult, type RuleField } from "shared";
import { api } from "../api";
import { caseKey, putProgram, type CaseProgram } from "../caseApi";
import { useProgramPanel } from "../programPanel";

const FIELD_LABELS: Record<RuleField, string> = {
  age: "Age", household_size: "Household size", household_income: "Household income", has_child_under: "Children's ages",
  pregnant: "Pregnant", disabled: "Disability", veteran: "Veteran status", senior: "Senior (65+)", employment_status: "Employment", state: "State",
};
const STATUS_TEXT: Record<MatchResult["status"], string> = {
  potentially_eligible: "May qualify", insufficient_information: "Need more information", unlikely_eligible: "Unlikely to qualify",
};
/** Server reason that says the same as the panel's own "Above the usual limit" line. */
const INDICATIVE_NOT_MET_REASON = "Above the usual limit, but may still qualify through other routes.";
function shownReasons(r: MatchResult): string[] {
  return r.indicative?.state === "not_met" ? r.reasons.filter((x) => x !== INDICATIVE_NOT_MET_REASON) : r.reasons;
}
const EMPLOYMENT = [["employed", "Employed"], ["unemployed", "Unemployed"], ["self_employed", "Self-employed"], ["retired", "Retired"], ["unable_to_work", "Unable to work"]];
const AGES = [["under18", "Under 18"], ["18_24", "18 to 24"], ["25_49", "25 to 49"], ["50_64", "50 to 64"], ["65_plus", "65 or older"]];
const FLAGS = [["pregnant", "Pregnant"], ["disabled", "Disabled"], ["veteran", "Veteran"], ["senior", "Senior"]] as const;

/** Match form plus results. The form prefills state, ZIP, problem and urgency from the case; nothing is stored. */
export function MatchPanel({ caseView, programs, readOnly = false }: { caseView: CaseView; programs: CaseProgram[]; readOnly?: boolean }) {
  const qc = useQueryClient();
  const panel = useProgramPanel();
  const [size, setSize] = useState("");
  const [income, setIncome] = useState("");
  const [period, setPeriod] = useState("");
  const [employment, setEmployment] = useState("");
  const [age, setAge] = useState("");
  const [kids, setKids] = useState("");
  const [flags, setFlags] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<string | null>(null);

  const state = caseView.customer.stateCode ?? undefined;
  const match = useMutation({ mutationFn: (body: MatchInput) => api<MatchResponse>("/api/match", { method: "POST", body }) });
  const set = useMutation({
    mutationFn: (v: { r: MatchResult; status: CaseProgram["status"] }) =>
      putProgram(caseView.id, programs.find((p) => p.programId === v.r.programId), v.r.programId, v.status, state ?? null),
    onSuccess: () => qc.invalidateQueries({ queryKey: caseKey(caseView.id) }),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!state) { setError("Set the customer's state on the case first."); return; }
    if (income.trim() && !period) { setError("Choose monthly or yearly for the income."); return; }
    const ages = kids.split(",").map((s) => s.trim()).filter(Boolean).map(Number);
    if (ages.some((n) => !Number.isInteger(n) || n < 0 || n > 18)) { setError("Children's ages must be whole numbers from 0 to 18, separated by commas."); return; }
    const body: MatchInput = {
      state, urgency: caseView.urgency,
      ...(caseView.customer.zip ? { zip: caseView.customer.zip } : {}),
      ...(caseView.problemId ? { problemId: caseView.problemId } : {}),
      ...(size ? { householdSize: Number(size) } : {}),
      ...(income.trim() ? { income: Number(income), incomePeriod: period as "monthly" | "yearly" } : {}),
      ...(employment ? { employment: employment as MatchInput["employment"] } : {}),
      ...(age ? { ageRange: age as MatchInput["ageRange"] } : {}),
      ...(ages.length ? { childrenUnder: ages } : {}),
      ...Object.fromEntries(FLAGS.filter(([k]) => flags[k]).map(([k]) => [k, true])),
    };
    match.mutate(body);
  };

  // The server puts emergency programs first; keep that even if the order ever changes.
  const results = match.data?.results.slice().sort((a, b) => Number(b.emergency) - Number(a.emergency));
  const inCase = (id: string) => programs.some((p) => p.programId === id);
  return (
    <section className="card" aria-label="Find programs">
      <h2 className="mb-2 text-sm font-semibold">Find programs</h2>
      <form onSubmit={submit} className="grid grid-cols-2 gap-2 text-sm">
        <div><label className="label" htmlFor="m-size">Household size</label>
          <input id="m-size" className="input" type="number" min={1} max={30} value={size} onChange={(e) => setSize(e.target.value)} /></div>
        <div className="flex gap-2">
          <div className="flex-1"><label className="label" htmlFor="m-income">Household income</label>
            <input id="m-income" className="input" type="number" min={0} value={income} onChange={(e) => setIncome(e.target.value)} /></div>
          <div className="w-28"><label className="label" htmlFor="m-period">Income period</label>
            <select id="m-period" className="input" value={period} onChange={(e) => setPeriod(e.target.value)}>
              <option value="">Choose…</option><option value="monthly">Monthly</option><option value="yearly">Yearly</option>
            </select></div>
        </div>
        <div><label className="label" htmlFor="m-emp">Employment</label>
          <select id="m-emp" className="input" value={employment} onChange={(e) => setEmployment(e.target.value)}>
            <option value="">Not asked</option>{EMPLOYMENT.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></div>
        <div><label className="label" htmlFor="m-age">Age range</label>
          <select id="m-age" className="input" value={age} onChange={(e) => setAge(e.target.value)}>
            <option value="">Not asked</option>{AGES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></div>
        <div className="col-span-2"><label className="label" htmlFor="m-kids">Children's ages (comma list)</label>
          <input id="m-kids" className="input" placeholder="e.g. 2, 7" value={kids} onChange={(e) => setKids(e.target.value)} /></div>
        <div className="col-span-2 flex flex-wrap gap-4">
          {FLAGS.map(([k, l]) => (
            <label key={k} className="flex items-center gap-1.5"><input type="checkbox" checked={!!flags[k]} onChange={(e) => setFlags({ ...flags, [k]: e.target.checked })} /> {l}</label>
          ))}
        </div>
        <div className="col-span-2"><button type="submit" className="btn-primary" disabled={match.isPending}>Find programs</button></div>
      </form>
      {error && <p role="alert" className="mt-2 text-sm text-red-600">{error}</p>}
      {match.isError && <p role="alert" className="mt-2 text-sm text-red-600">Could not look up programs. Try again.</p>}
      {results && (
        <div className="mt-4">
          <p className="mb-2 rounded-md bg-slate-100 px-3 py-2 text-sm font-medium dark:bg-slate-800">{MATCH_DISCLAIMER}</p>
          {results.length === 0 && <p className="text-sm text-slate-500">
            {caseView.problemId ? "No programs found for this problem and state." : "Choose a problem on the case first."}</p>}
          <ul className="space-y-2">
            {results.map((r) => (
              <li key={r.programId} aria-label={`result ${r.name}`} className={`rounded-md border p-3 text-sm ${r.emergency ? "border-red-400 bg-red-50 dark:bg-red-950" : "border-slate-200 dark:border-slate-800"}`}>
                <div className="flex flex-wrap items-center gap-2">
                  {r.emergency && <span className="rounded bg-red-600 px-1.5 py-0.5 text-xs font-bold text-white">Emergency</span>}
                  <button type="button" className="font-semibold text-brand-700 hover:underline dark:text-brand-100" onClick={() => panel.open(r.slug, state)}>{r.name}</button>
                  <span className="text-xs text-slate-500">{STATUS_TEXT[r.status]}</span>
                  {r.indicative?.state === "met" && (
                    <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-xs font-medium text-emerald-800 dark:bg-emerald-900 dark:text-emerald-100">Likely meets the usual limit</span>)}
                </div>
                {r.indicative?.state === "not_met" && (
                  <div className="mt-1">
                    <p className="font-medium text-amber-800 dark:text-amber-200">Above the usual limit — may still qualify</p>
                    {r.indicative.notes.length > 0 && (
                      <div className="mt-1"><span className="font-medium">Other routes to check</span>
                        <ul className="list-disc pl-5 text-slate-700 dark:text-slate-300">{r.indicative.notes.map((n, i) => <li key={i}>{n}</li>)}</ul></div>
                    )}
                  </div>
                )}
                {shownReasons(r).length > 0 && <ul className="mt-1 list-disc pl-5 text-slate-700 dark:text-slate-300">{shownReasons(r).map((x) => <li key={x}>{x}</li>)}</ul>}
                {r.askFor.length > 0 && (
                  <div className="mt-1"><span className="font-medium">Ask the caller:</span>
                    <ul className="list-disc pl-5">{r.askFor.map((f) => <li key={f}>{FIELD_LABELS[f]}</li>)}</ul></div>
                )}
                {!readOnly && (
                  <div className="mt-2 flex gap-2">
                    {!inCase(r.programId) && <button type="button" className="btn-ghost border border-slate-300" disabled={set.isPending}
                      onClick={() => set.mutate({ r, status: "recommended" })}>Add to case</button>}
                    {(!inCase(r.programId) || programs.find((p) => p.programId === r.programId)?.status === "recommended") && (
                      <button type="button" className="btn-ghost border border-slate-300" disabled={set.isPending}
                        onClick={() => set.mutate({ r, status: "referred" })}>Mark referred</button>)}
                  </div>
                )}
              </li>
            ))}
          </ul>
          {set.isError && <p role="alert" className="mt-2 text-sm text-red-600">Could not update the case.</p>}
        </div>
      )}
    </section>
  );
}
